import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useContext, useEffect, useRef, useState } from 'react'
import {
  commentColorMap,
  Comment,
  CommentWithReplies,
} from '../../models/Comment'
import CommentReply from './CommentReplyComponent'
import { useDispatch, useSelector } from 'react-redux'
import { calculateAge } from '../../utils/Common'
import {
  addCommentReplyAsync,
  updateCommentAsync,
} from '../../services/commentsService'
import { ConstructionTab } from '../../reducers/construction'
import { RootState } from '../../store'
import { AbilityContext, Can } from '../../utils/AbilityUtil'
import {
  AttributeActionNames,
  AttributeResourceNames,
} from '../../models/AccessControl'

interface CommentProps {
  tab: ConstructionTab
  comment: CommentWithReplies
  index?: number
  showReplyButton?: boolean
  showAllReplies?: boolean
  showReactions?: boolean
  showResolve?: boolean
  darkMode?: boolean
}

const CommentComponent: React.FC<CommentProps> = ({
  tab,
  comment,
  index,
  showReplyButton,
  showAllReplies,
  showReactions,
  showResolve,
  darkMode,
}) => {
  const dispatch = useDispatch<any>()
  const [expandReplies, setExpandReplies] = useState(false)
  const [commentAge, setCommentAge] = useState('')
  const replyTextFieldRef = useRef<HTMLSelectElement>()
  const replyAuthor = useSelector((state: RootState) => state.auth.user)
  const commentAuthor = useSelector((state: RootState) =>
    state.organization.users?.find((user) => user.id === comment.author_id),
  )

  const ability = useContext(AbilityContext)

  const handleRepliesClick = () => {
    setExpandReplies(!expandReplies)
  }

  const handleReplyClick = () => {
    setExpandReplies(true)
    setTimeout(() => {
      if (replyTextFieldRef.current) {
        replyTextFieldRef.current?.focus()
      }
    }, 100)
  }

  const addCommentReply = () => {
    if (replyTextFieldRef.current) {
      const replyText = replyTextFieldRef.current.value
      if (!!replyAuthor) {
        const reply: Comment = {
          content: replyText,
          author_id: replyAuthor?.id,
          discipline: null,
        }
        if (comment.id !== undefined) {
          dispatch(addCommentReplyAsync({ tab, commentId: comment.id, reply }))
        } else {
          console.error(
            'Error: can not add comment reply, comment id is unknown',
          )
        }
      } else {
        console.error('Error: can not add comment reply, author is unknown')
      }
      replyTextFieldRef.current.value = ''
    }
  }

  const resolveCommentClick = () => {
    const newComment = { isResolved: true }
    if (comment.id !== undefined) {
      dispatch(updateCommentAsync({ tab, commentId: comment.id, newComment }))
    } else {
      console.error('Error: can not add comment reply, comment id is unknown')
    }
  }

  useEffect(() => {
    const updateAge = () => {
      if (!!comment.createdAt) {
        const age = calculateAge(comment.createdAt)
        setCommentAge(age)
      }
    }

    const intervalId = setInterval(updateAge, 5000)

    return () => clearInterval(intervalId)
  }, [comment])

  return (
    <Can I={AttributeActionNames.Read} a={AttributeResourceNames.Comments}>
      <Stack sx={{ p: '1px' }}>
        <Box
          sx={{
            display: 'flex',
            mb: '16px',
            mt: index === 0 || isNaN(index!) ? 0 : '16px',
          }}
        >
          <Stack sx={{ width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: '7px',
              }}
            >
              <Box
                columnGap='8px'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <svg
                  width='20'
                  height='22'
                  viewBox='0 0 20 22'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  xmlnsXlink='http://www.w3.org/1999/xlink'
                >
                  <rect
                    y='0.820312'
                    width='20'
                    height='20.2648'
                    rx='10'
                    fill='#95BAED'
                  />
                  <rect
                    y='0.820312'
                    width='20'
                    height='20.2648'
                    rx='10'
                    fill='url(#pattern0_1871_2452)'
                  />
                  <defs>
                    <pattern
                      id='pattern0_1871_2452'
                      patternContentUnits='objectBoundingBox'
                      width='1'
                      height='1'
                    >
                      <use
                        xlinkHref='#image0_1871_2452'
                        transform='matrix(0.00506619 0 0 0.005 -0.00661917 0)'
                      />
                    </pattern>
                    <image
                      id='image0_1871_2452'
                      width='200'
                      height='200'
                      xlinkHref='data:image/png;base64,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'
                    />
                  </defs>
                </svg>
                <Typography
                  sx={{
                    color: darkMode ? '#fff' : '#212121',
                    fontSize: '12px',
                    fontWeight: 500,
                    lineHeight: 'normal',
                  }}
                >
                  {commentAuthor?.user_name}
                </Typography>
              </Box>
              <Box gap={'7px'} sx={{ display: 'flex' }}>
                {!!comment.discipline && (
                  <Box
                    sx={{
                      width: '114px',
                      fontSize: '10px',
                      textAlign: 'center',
                      borderRadius: '72px',
                      color: '#fff',
                      fontWeight: 500,
                      lineHeight: 'normal',
                      background: commentColorMap[comment.discipline],
                    }}
                  >
                    {comment.discipline}
                  </Box>
                )}
                {showResolve && (
                  <IconButton
                    disableRipple
                    disabled={ability.cannot(
                      AttributeActionNames.Update,
                      AttributeResourceNames.Comments,
                    )}
                    onClick={resolveCommentClick}
                    sx={{
                      borderRadius: '12.727px',
                      background: '#FFF',
                      boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.25)',
                      height: '14px',
                      width: '14px',
                      padding: 0,
                      mr: '6px',
                    }}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='7'
                      height='5'
                      viewBox='0 0 7 5'
                      fill='none'
                    >
                      <path
                        d='M0.398536 2.27133L1.82363 4.30333L6.51335 1.27161'
                        stroke='#8C9809'
                        strokeWidth='0.6'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </IconButton>
                )}
                <Typography
                  sx={{
                    color: darkMode ? '#BBBCBC' : '#97999B',
                    fontSize: '12px',
                    lineHeight: 'normal',
                  }}
                >
                  {commentAge}
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                color: darkMode ? '#fff' : '#212121',
                fontSize: '14px',
                lineHeight: '22px',
                width: '100%',
                textAlign: 'left',
              }}
            >
              {comment.content}
            </Typography>
            {showReactions && (
              <Box>
                <Button
                  disableRipple
                  size='small'
                  sx={{
                    fontSize: '7.718px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '7.636px',
                    color: '#212121',
                    minWidth: '28px',
                    height: '14px',
                    borderRadius: '12.727px',
                    background: '#FFF',
                    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.25)',
                    mr: '4px',
                  }}
                >
                  ⭐️ 1
                </Button>
                <IconButton
                  disableRipple
                  sx={{
                    borderRadius: '12.727px',
                    background: '#FFF',
                    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.25)',
                    height: '14px',
                    width: '14px',
                    padding: 0,
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='8'
                    height='8'
                    viewBox='0 0 8 8'
                    fill='none'
                  >
                    <path
                      d='M2.86399 3.04563V2.72745C2.68826 2.72745 2.54581 2.8699 2.54581 3.04563H2.86399ZM2.89569 3.04563H3.21387C3.21387 2.8699 3.07141 2.72745 2.89569 2.72745V3.04563ZM4.77308 3.04563V2.72745C4.59736 2.72745 4.4549 2.8699 4.4549 3.04563H4.77308ZM4.80126 3.04563H5.11944C5.11944 2.8699 4.97698 2.72745 4.80126 2.72745V3.04563ZM2.89569 3.07111V3.38929C3.07141 3.38929 3.21387 3.24684 3.21387 3.07111H2.89569ZM2.86399 3.07111H2.54581C2.54581 3.24684 2.68826 3.38929 2.86399 3.38929V3.07111ZM4.80126 3.07111V3.38929C4.97698 3.38929 5.11944 3.24684 5.11944 3.07111H4.80126ZM4.77308 3.07111H4.4549C4.4549 3.24684 4.59736 3.38929 4.77308 3.38929V3.07111ZM2.98604 4.80023C2.89355 4.65082 2.69744 4.60467 2.54802 4.69716C2.39861 4.78965 2.35246 4.98575 2.44495 5.13517L2.98604 4.80023ZM5.19213 5.13517C5.28462 4.98575 5.23847 4.78965 5.08905 4.69716C4.93963 4.60467 4.74353 4.65082 4.65104 4.80023L5.19213 5.13517ZM6.68217 4.00018C6.68217 5.58172 5.40008 6.86381 3.81854 6.86381V7.50018C5.75153 7.50018 7.31854 5.93318 7.31854 4.00018H6.68217ZM3.81854 6.86381C2.23699 6.86381 0.954901 5.58172 0.954901 4.00018H0.318537C0.318537 5.93318 1.88554 7.50018 3.81854 7.50018V6.86381ZM0.954901 4.00018C0.954901 2.41864 2.23699 1.13654 3.81854 1.13654V0.500177C1.88554 0.500177 0.318537 2.06718 0.318537 4.00018H0.954901ZM3.81854 1.13654C5.40008 1.13654 6.68217 2.41864 6.68217 4.00018H7.31854C7.31854 2.06718 5.75153 0.500177 3.81854 0.500177V1.13654ZM2.86399 3.36381H2.89569V2.72745H2.86399V3.36381ZM4.77308 3.36381H4.80126V2.72745H4.77308V3.36381ZM2.5775 3.04563V3.07111H3.21387V3.04563H2.5775ZM2.89569 2.75293H2.86399V3.38929H2.89569V2.75293ZM3.18217 3.07111V3.04563H2.54581V3.07111H3.18217ZM4.48308 3.04563V3.07111H5.11944V3.04563H4.48308ZM4.80126 2.75293H4.77308V3.38929H4.80126V2.75293ZM5.09126 3.07111V3.04563H4.4549V3.07111H5.09126ZM3.81854 5.27289C3.47233 5.27289 3.16399 5.08772 2.98604 4.80023L2.44495 5.13517C2.73097 5.59723 3.23727 5.90925 3.81854 5.90925V5.27289ZM4.65104 4.80023C4.47308 5.08772 4.16474 5.27289 3.81854 5.27289V5.90925C4.3998 5.90925 4.90611 5.59723 5.19213 5.13517L4.65104 4.80023Z'
                      fill='#616161'
                    />
                  </svg>
                </IconButton>
              </Box>
            )}
            {(showAllReplies || showReplyButton) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between',
                  mt: '22px',
                }}
              >
                {showAllReplies && (
                  <Button
                    variant='text'
                    onClick={handleRepliesClick}
                    sx={{
                      color: darkMode ? '#C8DFFF' : '#1705E5',
                      fontSize: '10px',
                      lineHeight: 'normal',
                      textTransform: 'none',
                      padding: 0,
                      visibility: comment.replies.length ? 'flex' : 'hidden',
                      alignItems: 'flex-start',
                      minWidth: '1px',
                    }}
                  >
                    {expandReplies
                      ? 'Hide replies'
                      : `${comment.replies.length} ${comment.replies.length === 1 ? 'reply' : 'replies'}`}
                  </Button>
                )}
                {showReplyButton && !expandReplies && !comment.isResolved && (
                  <Button
                    disableRipple
                    disabled={ability.cannot(
                      AttributeActionNames.Update,
                      AttributeResourceNames.Comments,
                    )}
                    onClick={handleReplyClick}
                    sx={{
                      borderRadius: '4px',
                      background: '#E5F0FF',
                      textTransform: 'capitalize',
                      color: darkMode ? '#1705E5' : '#223C5F',
                    }}
                  >
                    Reply
                  </Button>
                )}
              </Box>
            )}
          </Stack>
        </Box>
        <Box>
          {expandReplies || !showAllReplies ? (
            <Box sx={{ mb: isNaN(index!) ? 0 : '16px' }}>
              {expandReplies &&
                comment.replies.map((reply, index) => (
                  <CommentReply
                    key={index}
                    reply={reply}
                    darkMode={!!darkMode}
                  ></CommentReply>
                ))}
              {!comment.isResolved && (
                <TextField
                  disabled={ability.cannot(
                    AttributeActionNames.Update,
                    AttributeResourceNames.Comments,
                  )}
                  variant='outlined'
                  placeholder='Reply'
                  inputRef={replyTextFieldRef}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <Box
                          sx={{
                            height: '22px',
                            width: '1px',
                            backgroundColor: '#ccc',
                            marginRight: '4px',
                          }}
                        />
                        <IconButton
                          disableRipple
                          disabled={ability.cannot(
                            AttributeActionNames.Update,
                            AttributeResourceNames.Comments,
                          )}
                          onClick={addCommentReply}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='15'
                            height='14'
                            viewBox='0 0 15 14'
                            fill='none'
                          >
                            <path
                              d='M9.88797 1L14 5.0877M14 5.0877L9.88797 9.1754M14 5.0877L5 5.0877C2.79086 5.0877 1 6.87856 1 9.0877L1 13'
                              stroke={isNaN(index!) ? '#1705E5' : '#B5B5B5'}
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { padding: '0', width: '100%', background: '#F2F2F2' },
                  }}
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-input::placeholder': {
                      color: '#B5B5B5',
                      opacity: 1,
                      fontSize: '14px',
                    },
                    '& .MuiInputBase-input': {
                      padding: '8px 11px',
                    },
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      color: '#04002E',
                      '& fieldset': {
                        borderColor: 'transparent',
                      },
                      '&:hover fieldset': {
                        borderColor: 'transparent',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'transparent',
                      },
                    },
                    flexGrow: 1,
                  }}
                />
              )}
            </Box>
          ) : (
            <></>
          )}
        </Box>
        {!isNaN(index!) && (
          <Divider
            flexItem
            sx={{ backgroundColor: darkMode ? '#181818' : '#BBBCBC' }}
          />
        )}
      </Stack>
    </Can>
  )
}

export default CommentComponent
